import { forwardRef } from 'react'
import styled from '@emotion/styled'
import ReactQuill from 'react-quill'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { ToolbarOption } from './htmlEditor.types'
import { getToolbarOptions } from './htmlEditor.utils'
import { SPACING } from 'constants/spacing.constants'

import 'react-quill/dist/quill.snow.css'

const emptyFunc = () => undefined

const DEFAULT_TOOLBAR_OPTIONS: ToolbarOption[] = [
    'bold',
    'italic',
    'underline',
    'link',
    'numberedList',
    'bulletList',
]

const StyledQuill = styled(ReactQuill)<{
    fullHeight: boolean
    fixedHeight?: string
}>`
    width: 100%;
    display: flex;
    flex-direction: column;

    ${(p) => p.fullHeight && `height: 100%;`}

    .ql-toolbar {
        border-top-left-radius: 0.6rem;
        border-top-right-radius: 0.6rem;

        .ql-formats {
            .ql-active {
                .ql-stroke,
                .ql-fill {
                    stroke: ${DESIGN_TOKENS.text.foreground};
                }
            }

            button {
                &:hover {
                    .ql-stroke,
                    .ql-fill {
                        stroke: ${DESIGN_TOKENS.text.foreground};
                    }
                }
            }

            .ql-stroke,
            .ql-fill {
                stroke: ${DESIGN_TOKENS.text.mutedForeground};
            }
        }
    }

    .ql-container {
        border-bottom-left-radius: 0.6rem;
        border-bottom-right-radius: 0.6rem;
        ${(p) =>
            p.fixedHeight
                ? `
                height: ${p.fixedHeight}; /* Apply the provided fixed height */
                overflow: hidden; /* Prevent overflow of the container */
            `
                : `
                min-height: 8rem; /* Default behavior */
            `}
    }

    .ql-toolbar,
    .ql-container {
        font-family: Inter;
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 400;
        line-height: 2rem;
        color: ${DESIGN_TOKENS.text.foreground};
        background-color: ${DESIGN_TOKENS.background.primary};
    }

    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
        border: 1px solid ${DESIGN_TOKENS.border.input};
    }

    .ql-editor {
        ${(p) =>
            p.fixedHeight
                ? `
                overflow-y: auto; /* Enable vertical scrolling */
                max-height: 100%; /* Ensure editor fits within the container */
                padding: ${SPACING.p2};
            `
                : `
                overflow-y: visible; /* Default behavior */
                padding: ${SPACING.p2};
            `}

        p:not(:first-child) {
            margin-top: ${SPACING.p2};
        }

        p:not(:last-child) {
            margin-bottom: ${SPACING.p2};
        }
    }

    .ql-editor[data-placeholder]::before {
        color: ${DESIGN_TOKENS.text.mutedForeground};
    }
`

type HtmlEditorProps = {
    readOnly?: boolean
    fullHeight?: boolean
    fixedHeight?: string // Accepts height as a string (e.g., '300px', '16rem')
    value?: string
    placeholder?: string
    className?: string
    toolbarOptions?: ToolbarOption[]
    onBlur?: () => void
    onChange?: (htmlContent: string) => void
}

export const HtmlEditor = forwardRef<ReactQuill, HtmlEditorProps>(
    (
        {
            readOnly = false,
            fullHeight = false,
            fixedHeight,
            value,
            placeholder,
            className,
            toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
            onBlur = emptyFunc,
            onChange = emptyFunc,
        }: HtmlEditorProps,
        ref
    ) => {
        return (
            <StyledQuill
                ref={ref}
                readOnly={readOnly}
                fullHeight={fullHeight}
                fixedHeight={fixedHeight}
                value={value}
                placeholder={placeholder}
                className={className}
                modules={{
                    toolbar: toolbarOptions.length
                        ? getToolbarOptions(toolbarOptions)
                        : null,
                }}
                onBlur={onBlur}
                onChange={onChange}
            />
        )
    }
)
